/**
 * Verification Constants and Configurations
 * Centralized configuration for document types, trust levels, and UI settings
 */

import { DocumentConfig, TrustLevel, DocumentType } from '../types/verification';

// Document Type Configurations
export const DOCUMENT_CONFIGS: Record<DocumentType, DocumentConfig> = {
    // Identity Documents
    national_id: {
        type: 'national_id',
        category: 'identity',
        displayName: 'National ID Card',
        description: 'Government-issued national identification card (both front and back required)',
        acceptedFormats: ['image/jpeg', 'image/png', 'image/webp', 'application/pdf'],
        maxSizeBytes: 10 * 1024 * 1024, // 10MB
        required: true,
        requiresFrontBack: true,
        icon: 'identification'
    },
    passport: {
        type: 'passport',
        category: 'identity',
        displayName: 'International Passport',
        description: 'Valid international passport (photo page)',
        acceptedFormats: ['image/jpeg', 'image/png', 'image/webp', 'application/pdf'],
        maxSizeBytes: 10 * 1024 * 1024,
        required: true,
        requiresFrontBack: false,
        icon: 'document'
    },
    drivers_license: {
        type: 'drivers_license',
        category: 'identity',
        displayName: "Driver's License",
        description: 'Valid driver license with photo (both front and back required)',
        acceptedFormats: ['image/jpeg', 'image/png', 'image/webp', 'application/pdf'],
        maxSizeBytes: 10 * 1024 * 1024,
        required: false,
        requiresFrontBack: true,
        icon: 'identification'
    },
    voters_card: {
        type: 'voters_card',
        category: 'identity',
        displayName: "Voter's Card",
        description: 'Government-issued voter registration card (both front and back required)',
        acceptedFormats: ['image/jpeg', 'image/png', 'image/webp', 'application/pdf'],
        maxSizeBytes: 10 * 1024 * 1024,
        required: false,
        requiresFrontBack: true,
        icon: 'identification'
    },

    // Address Verification Documents
    utility_bill: {
        type: 'utility_bill',
        category: 'address',
        displayName: 'Utility Bill',
        description: 'Recent utility bill (electricity, water, gas) - max 3 months old',
        acceptedFormats: ['image/jpeg', 'image/png', 'image/webp', 'application/pdf'],
        maxSizeBytes: 10 * 1024 * 1024,
        required: true,
        requiresFrontBack: false,
        icon: 'document-text'
    },
    bank_statement: {
        type: 'bank_statement',
        category: 'address',
        displayName: 'Bank Statement',
        description: 'Recent bank statement showing your address - max 3 months old',
        acceptedFormats: ['image/jpeg', 'image/png', 'image/webp', 'application/pdf'],
        maxSizeBytes: 10 * 1024 * 1024,
        required: false,
        requiresFrontBack: false,
        icon: 'document-text'
    },
    government_document: {
        type: 'government_document',
        category: 'address',
        displayName: 'Government Address Document',
        description: 'Any government-issued document showing your address',
        acceptedFormats: ['image/jpeg', 'image/png', 'image/webp', 'application/pdf'],
        maxSizeBytes: 10 * 1024 * 1024,
        required: false,
        requiresFrontBack: false,
        icon: 'document-text'
    },
    rental_agreement: {
        type: 'rental_agreement',
        category: 'address',
        displayName: 'Rental Agreement',
        description: 'Valid rental/lease agreement',
        acceptedFormats: ['image/jpeg', 'image/png', 'image/webp', 'application/pdf'],
        maxSizeBytes: 10 * 1024 * 1024,
        required: false,
        requiresFrontBack: false,
        icon: 'home'
    },

    // Business Documents
    business_registration: {
        type: 'business_registration',
        category: 'business',
        displayName: 'Business Registration',
        description: 'Certificate of business registration or incorporation',
        acceptedFormats: ['image/jpeg', 'image/png', 'image/webp', 'application/pdf'],
        maxSizeBytes: 10 * 1024 * 1024,
        required: false,
        requiresFrontBack: false,
        icon: 'building-office'
    },
    tax_certificate: {
        type: 'tax_certificate',
        category: 'business',
        displayName: 'Tax Registration',
        description: 'Tax identification number certificate',
        acceptedFormats: ['image/jpeg', 'image/png', 'image/webp', 'application/pdf'],
        maxSizeBytes: 10 * 1024 * 1024,
        required: false,
        requiresFrontBack: false,
        icon: 'receipt-tax'
    }
};

// Trust Level Configurations
export const TRUST_LEVELS: TrustLevel[] = [
    {
        name: 'Beginner',
        minScore: 0,
        maxScore: 29,
        benefits: ['Basic platform access', 'Post gigs up to $100', 'Apply for gigs up to $200'],
        color: 'bg-neutral-500',
        icon: 'user'
    },
    {
        name: 'Intermediate',
        minScore: 30,
        maxScore: 49,
        benefits: ['Post gigs up to $300', 'Apply for gigs up to $500', 'Enhanced profile visibility'],
        color: 'bg-blue-500',
        icon: 'user-plus'
    },
    {
        name: 'Advanced',
        minScore: 50,
        maxScore: 69,
        benefits: ['Post gigs up to $500', 'Apply for gigs up to $1000', 'Priority search ranking'],
        color: 'bg-green-500',
        icon: 'star'
    },
    {
        name: 'Expert',
        minScore: 70,
        maxScore: 89,
        benefits: ['Post gigs up to $2000', 'Apply for gigs up to $3000', 'Featured listings'],
        color: 'bg-purple-500',
        icon: 'shield-check'
    },
    {
        name: 'Elite',
        minScore: 90,
        maxScore: 100,
        benefits: ['Unlimited gig values', 'VIP support', 'Platform ambassador program'],
        color: 'bg-gradient-to-r from-yellow-400 to-orange-500',
        icon: 'trophy'
    }
];

// Verification Benefits for Verified Users
export const VERIFIED_USER_BENEFITS = [
    {
        icon: 'check-badge',
        title: 'Verified Badge',
        description: 'Display a verified badge on your profile and gigs'
    },
    {
        icon: 'currency-dollar',
        title: '50% Fee Reduction',
        description: 'Pay only half the platform fees on all transactions'
    },
    {
        icon: 'infinity',
        title: 'Unlimited Gig Values',
        description: 'Post and apply for gigs of any value'
    },
    {
        icon: 'star',
        title: 'Premium Access',
        description: 'Access exclusive high-value gig categories'
    },
    {
        icon: 'chat-bubble-left-ellipsis',
        title: 'Priority Support',
        description: 'Get faster response times from customer support'
    },
    {
        icon: 'megaphone',
        title: 'Featured Listings',
        description: 'Higher visibility in search results and featured sections'
    }
];

// File Upload Constraints
export const UPLOAD_CONSTRAINTS = {
    maxFileSize: 10 * 1024 * 1024, // 10MB
    maxTotalSize: 50 * 1024 * 1024, // 50MB per user
    supportedFormats: ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'application/pdf'],
    maxFilesPerType: 2, // Max 2 files per document type
    imageQuality: {
        minWidth: 600,
        minHeight: 400,
        minDPI: 150 // recommended
    }
};

// UI Messages and Labels
export const VERIFICATION_MESSAGES = {
    upload: {
        success: 'Document uploaded successfully!',
        error: 'Failed to upload document. Please try again.',
        processing: 'Processing your document...',
        invalidFormat: 'Please upload a valid file format (JPEG, PNG, WebP, or PDF)',
        fileTooLarge: 'File size must be less than 10MB',
        lowQuality: 'Image quality is too low. Please upload a clearer image.'
    },
    status: {
        unverified: 'Not verified - You can still use the platform with basic access',
        incomplete: 'Verification incomplete - Upload required documents to get verified',
        pending: 'Under review - We\'re reviewing your documents',
        pending_review: 'Pending review - We\'re reviewing your documents',
        under_review: 'Being reviewed - Our team is verifying your documents',
        verified: 'Verified - Enjoy all premium benefits!',
        rejected: 'Documents rejected - Please resubmit with corrections',
        expired: 'Verification expired - Please update your documents'
    },
    trustScore: {
        increase: 'Great job! Your trust score increased',
        decrease: 'Your trust score decreased. Complete gigs successfully to improve it',
        levelUp: 'Congratulations! You reached a new trust level'
    }
};

// Status Colors for UI
export const STATUS_COLORS = {
    unverified: { bg: 'bg-neutral-100', text: 'text-neutral-700', border: 'border-neutral-200' },
    incomplete: { bg: 'bg-yellow-100', text: 'text-yellow-700', border: 'border-yellow-200' },
    pending: { bg: 'bg-blue-100', text: 'text-blue-700', border: 'border-blue-200' },
    pending_review: { bg: 'bg-blue-100', text: 'text-blue-700', border: 'border-blue-200' },
    under_review: { bg: 'bg-blue-100', text: 'text-blue-700', border: 'border-blue-200' },
    verified: { bg: 'bg-green-100', text: 'text-green-700', border: 'border-green-200' },
    rejected: { bg: 'bg-red-100', text: 'text-red-700', border: 'border-red-200' },
    expired: { bg: 'bg-orange-100', text: 'text-orange-700', border: 'border-orange-200' }
};

// API Endpoints
export const VERIFICATION_ENDPOINTS = {
    status: '/api/verification/status',
    upload: '/api/documents/upload',
    trustHistory: '/api/verification/trust-history',
    requirements: '/api/verification/requirements',
    stats: '/api/verification/stats',
    checkPermission: '/api/verification/check-permission'
};